'use client';

import type { BlogFiltersProps } from '@routeroyal/theme-core';
import { motion } from 'framer-motion';
import { Funnel } from '@phosphor-icons/react';

/**
 * AllysBlogFilters - Modern Medium-inspired blog filters
 * Clean pill-style categories with minimal sort dropdown
 */
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysBlogFiltersProps extends BlogFiltersProps {
  messages?: Record<string, unknown>;
}

export function AllysBlogFilters({
  categories,
  selectedCategory,
  onCategoryChange,
  sortOptions,
  sortBy = 'latest',
  onSortChange,
  messages,
  className = '',
}: AllysBlogFiltersProps) {
  const blogMsg = messages?.blog as Record<string, unknown> | undefined;
  const filtersMsg = blogMsg?.filters as Record<string, unknown> | undefined;

  const resolvedSortOptions = sortOptions || [
    { label: safeGetString(filtersMsg?.latest, 'Latest'), value: 'latest' },
    { label: safeGetString(filtersMsg?.oldest, 'Oldest'), value: 'oldest' },
    { label: safeGetString(filtersMsg?.popular, 'Most Popular'), value: 'popular' },
  ];
  return (
    <div className={`[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Category Pills */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {/* All Categories Pill */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onCategoryChange(undefined)}
              className={`
                px-5 py-2.5 rounded-full text-sm font-medium
                transition-all duration-200
                ${!selectedCategory
                  ? 'bg-[#243462] text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }
              `}
            >
              {safeGetString(filtersMsg?.allCategories, 'All')}
            </motion.button>

            {/* Category Pills */}
            {categories.map((category) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onCategoryChange(category)}
                className={`
                  px-5 py-2.5 rounded-full text-sm font-medium
                  transition-all duration-200
                  ${selectedCategory === category
                    ? 'bg-[#243462] text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }
                `}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Sort Dropdown */}
        {onSortChange && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Funnel className="w-4 h-4" weight="bold" aria-hidden="true" />
              <label htmlFor="blog-sort-select" className="font-medium">{safeGetString(filtersMsg?.sortBy, 'Sort:')}</label>
            </div>
            <div className="relative">
              <select
                id="blog-sort-select"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                className="
                  appearance-none
                  pl-4 pr-10 py-2.5
                  bg-white
                  border border-gray-200
                  rounded-full
                  text-sm font-medium text-[#243462]
                  hover:border-gray-300
                  focus:outline-none
                  focus:ring-2
                  focus:ring-[#BBA339]/20
                  focus:border-[#BBA339]
                  transition-all
                  cursor-pointer
                "
              >
                {resolvedSortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {/* Custom Dropdown Arrow */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

